import { create } from "zustand";
import type { User, Plan } from "@/types";

// Plan hierarchy for checking access (keep for backwards compatibility)
export type PlanType = "free" | "starter" | "creator" | "pro" | "ultimate";

// Map API plan IDs to our plan types
export function mapPlanIdToType(planId: string): PlanType {
  if (planId.startsWith("starter")) return "starter";
  if (planId.startsWith("creator")) return "creator";
  if (planId.startsWith("pro")) return "pro";
  if (planId.startsWith("ultimate")) return "ultimate";
  return "free";
}

// Legacy plan structure for backwards compatibility with existing components
export interface LegacyPlan {
  id: PlanType;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  credits: number;
  features: string[];
  clerkSlug?: string;
}

// Correct video durations based on actual model capabilities
// Overrides incorrect API values
const CORRECT_VIDEO_DURATIONS: Record<string, number> = {
  starter: 6, // Standard model max (12s requires Creator)
  creator: 12, // Can use standard 12s or premium 10s
  pro: 12, // Same as creator
  ultimate: 12, // Same as creator
};

// Convert API plan to legacy format for component compatibility
export function convertApiPlanToLegacy(plan: Plan): LegacyPlan {
  const isAnnual = plan.billing_period_days === 365;
  const priceInDollars = plan.price_cents / 100;
  const planType = mapPlanIdToType(plan.id);

  // Use correct duration from our mapping, fallback to API value
  const maxDuration =
    CORRECT_VIDEO_DURATIONS[planType] ??
    plan.features.max_video_duration_seconds;

  // Calculate monthly credits - annual plans store total annual credits, divide by 12 to show per month
  const monthlyCredits = isAnnual
    ? Math.round(plan.credits_per_cycle / 12)
    : plan.credits_per_cycle;

  const features: string[] = [
    `${monthlyCredits.toLocaleString()} Credits/Month`,
  ];

  // Only show watermark status for starter/creator (pro and ultimate already have it)
  if (planType !== "pro" && planType !== "ultimate") {
    if (!plan.features.watermark) {
      features.push("No Watermarks");
    } else {
      features.push("Watermarked videos");
    }
  }

  features.push(`${maxDuration}s max video duration`);

  // Pro and Ultimate perks
  if (planType === "pro" || planType === "ultimate") {
    features.push("Unlimited concurrent generations");
    features.push("Buy extra credit packs anytime");
  }

  // -1 indicates unlimited history
  const historyDisplay =
    plan.features.history_days === -1 ? "∞" : plan.features.history_days;
  features.push(`${historyDisplay} days history`);

  return {
    id: mapPlanIdToType(plan.id),
    name: plan.name.replace(" Monthly", "").replace(" Annual", ""),
    monthlyPrice: isAnnual
      ? Math.round((priceInDollars / 12) * 100) / 100
      : priceInDollars,
    annualPrice: isAnnual ? priceInDollars : priceInDollars * 12,
    credits: monthlyCredits,
    features,
    clerkSlug: plan.clerk_slug,
  };
}

// Get annual price per month (annual total / 12)
export const getAnnualPricePerMonth = (annualPrice: number): number => {
  return Math.round((annualPrice / 12) * 100) / 100;
};

interface UserState {
  // API user data
  user: User | null;
  isLoading: boolean;

  // Computed/derived values for backwards compatibility
  plan: PlanType;
  credits: number;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  updateCredits: (credits: number) => void;

  // Legacy actions (kept for compatibility)
  setPlan: (plan: PlanType) => void;
  setCredits: (credits: number) => void;
}

export const useUserStore = create<UserState>((set) => ({
  user: null,
  isLoading: true,
  plan: "free",
  credits: 0,

  setUser: (user) =>
    set({
      user,
      plan: user ? mapPlanIdToType(user.plan_id) : "free",
      credits: user?.credit_balance ?? 0,
    }),

  setLoading: (isLoading) => set({ isLoading }),

  updateCredits: (credits) =>
    set((state) => ({
      credits,
      user: state.user ? { ...state.user, credit_balance: credits } : null,
    })),

  // Legacy actions for backwards compatibility
  setPlan: (plan) => set({ plan }),
  setCredits: (credits) => set({ credits }),
}));

// Helper to check if user has access to a plan level
export const hasAccessToPlanLevel = (
  userPlan: PlanType,
  requiredPlan: PlanType,
): boolean => {
  const hierarchy: PlanType[] = [
    "free",
    "starter",
    "creator",
    "pro",
    "ultimate",
  ];
  return hierarchy.indexOf(userPlan) >= hierarchy.indexOf(requiredPlan);
};

export const getPlanDetails = (planId: PlanType): LegacyPlan | undefined => {
  // This is now a fallback - prefer using real plan data from API
  const fallbackPlans: LegacyPlan[] = [
    {
      id: "free",
      name: "Free",
      monthlyPrice: 0,
      annualPrice: 0,
      credits: 30,
      features: [
        "30 credits/week",
        "Basic video generation",
        "Watermarked videos",
      ],
      clerkSlug: "free_user",
    },
    {
      id: "starter",
      name: "Starter",
      monthlyPrice: 29.99,
      annualPrice: 0,
      credits: 400,
      features: ["400 Credits/Month", "No Watermarks", "6s max video duration"],
      clerkSlug: "starter_new",
    },
    {
      id: "creator",
      name: "Creator",
      monthlyPrice: 59.99,
      annualPrice: 0,
      credits: 1000,
      features: [
        "1000 Credits/Month",
        "No Watermarks",
        "12s max video duration",
      ],
      clerkSlug: "creator_new",
    },
    {
      id: "pro",
      name: "Pro",
      monthlyPrice: 119.99,
      annualPrice: 0,
      credits: 2400,
      features: [
        "2,400 Credits/Month",
        "Unlimited concurrent generations",
        "Priority processing",
      ],
      clerkSlug: "pro_new",
    },
    {
      id: "ultimate",
      name: "Ultimate",
      monthlyPrice: 249.99,
      annualPrice: 0,
      credits: 5000,
      features: [
        "5,000 Credits/Month",
        "Unlimited concurrent generations",
      ],
      clerkSlug: "the_ultimate",
    },
  ];
  return fallbackPlans.find((p) => p.id === planId);
};

export const isFreePlan = (plan: PlanType): boolean => plan === "free";

/**
 * Check if user's plan requires showing watermarked URLs
 * Free and Starter plans show watermarked content
 */
export const shouldShowWatermarked = (plan: PlanType): boolean => {
  return plan === "free" || plan === "starter";
};

/**
 * Get the appropriate display URL based on user's plan
 * Shows watermarked URL for free/starter plans, original URL for creator+
 * @param asset - Object containing url and url_watermarked properties
 * @param plan - User's current plan type
 * @returns The URL to display (watermarked for free/starter, original otherwise)
 */
export const getDisplayUrl = <
  T extends { url: string; url_watermarked?: string | null },
>(
  asset: T,
  plan: PlanType,
): string => {
  if (shouldShowWatermarked(plan) && asset.url_watermarked) {
    return asset.url_watermarked;
  }
  return asset.url;
};
